import AvailablePromoCodesModal from "@/features/Common/components/AvailablePromoCodesModal";
import {
  AnalyticsEvent,
  trackApplicationEvent,
  trackEvent,
} from "@/services/analytics-adapter";
import {
  isFeatureEnabled,
  FeatureToggleIdentifier,
} from "@/services/feature-toggle-adapter";
import { useGlobalStore } from "@/store";
import { debugLogger } from "@/utils";
import { Button, Stack, Typography } from "@mui/material";
import { useMachine } from "@xstate/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams, useLocation } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import {
  GetMemberConsultationsItem,
  getConsultationPaymentRedirectURL,
  Member,
  MemberConsultationOutcome,
  MemberProductPaymentRatesChargeCode,
  MemberServiceOfferingType,
  MemberServiceOfferingUsage,
  MembershipDependent,
  ServiceRating,
  UserRating,
  VoucherFilterKey,
} from "../../../../services/core-api-adapter";
import Breakout from "../../../Common/components/Breakout";
import DefaultError from "../../../Common/components/DefaultError";
import FullscreenLoadingIndicator from "../../../Common/components/FullscreenLoadingIndicator";
import MaxWidthContainer from "../../../Common/components/MaxWidthContainer";
import { MedicalAssistanceDependantSelection } from "../../../Common/components/MedicalAssistanceDependantSelection";
import PaymentFailure from "../../../Common/components/PaymentFailure";
import ThankYou from "../../../Common/components/ThankYou";
import UserRatingFeedback from "../../../Common/components/UserRatingFeedback";
import UserRatingFeedbackError from "../../../Common/components/UserRatingFeedbackError";
import { ActiveConsultationOutstandingPayment } from "../ActiveConsultationOutstandingPayment";
import { ActiveConsultations } from "../ActiveConsultations";
import { ConsultationPrompt } from "../ConsultationPrompt";
import { ConsultationTypeSelection } from "../ConsultationTypeSelection";
import DirectToDoctorPrimarySymptoms from "../DirectToDoctorPrimarySymptoms";
import DoctorBookingConfirmation from "../DoctorBookingConfirmation";
import FindADoctor from "../FindADoctor";
import MedicalAssistanceFindAHospital from "../MedicalAssistanceFindAHospital";
import NurseConsultMoreInfo from "../NurseConsultMoreInfo";
import OutsideConsultationOperatingHours from "../OutsideConsultationOperatingHours";
import { Refund } from "../Refund";
import { eventNames, intercareFlowStateMachine } from "./machine";
import DocMedsBundlesInfo from "../DocMedsBundlesInfo";
import DocMedsDeliveryInfo from "../DocMedsDeliveryInfo";
import PaymentOptionSelection from "@/features/Common/components/PaymentOptionsSelection";
import VoucherSelection from "@/features/Common/components/VoucherSelection";
import OneVoucherRedemption from "@/features/Common/components/OneVoucherRedemption";
import ConsultationOrderSummary from "../ConsultationOrderSummary";
import PaymentReceipt from "@/features/Common/components/PaymentReceipt";
import {
  useInsightsAdapter,
  useTrackPaymentOnSuccess,
  MembershipType,
} from "@/services/insights-adapter";

const log = debugLogger.forMachine("IntercareMedicalAssistance");
const intercareInspector = debugLogger.createInspector(
  "IntercareMedicalAssistance"
);

export default function InterCareMedicalAssistanceFlow() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { id } = useParams();
  const { trackSelectConsultType } = useInsightsAdapter();
  const [searchParams] = useSearchParams();
  const { state } = useGlobalStore();
  const location = useLocation();

  const primarySymptomIds = searchParams.get("primarySymptomIds");
  const isDocMedsBundle = searchParams.get("isDocMedsBundle");
  const orderId = searchParams.get("orderId");
  const isFromMyPurchases = searchParams.get("orderSummary");
  const fromMyPurchasesPage = location.state?.fromMyPurchasesPage;

  const [stateMachineState, dispatchStateMachineEvent] = useMachine(
    intercareFlowStateMachine,
    {
      inspect: intercareInspector,
      input: {
        context: {
          queryParamConsultationId: id || orderId,
          primarySymptomIds: primarySymptomIds?.split(",") || [],
          queryParamDocMedsBundles: Boolean(isDocMedsBundle),
          currentUserMemberId: state.currentUser.memberId || null,
          isFromMyPurchases: Boolean(isFromMyPurchases),
          fromMyPurchasesPage: fromMyPurchasesPage,
        },
      },
    } as any
  );

  useTrackPaymentOnSuccess({
    isSuccessState:
      stateMachineState.matches("showOrderSummaryScreen") &&
      !!stateMachineState.context.paymentReceiptResponse?.paymentSuccess,
    receipt: stateMachineState.context.paymentReceiptResponse ?? null,
    chargeCode:
      stateMachineState.context.paymentReceiptResponse?.chargeCode ?? "",
    memberId:
      stateMachineState.context.paymentReceiptResponse?.patientMemberId ?? "",
  });

  function onDependentSelectionNextButtonClick(selectedMember: Member) {
    log.event("Dependent selected", {
      selectedMember: selectedMember,
      currentState: stateMachineState.value,
      isDirectToDoctorSelected:
        stateMachineState.context.isDirectToDoctorSelected,
      isNurseConsultationSelected:
        stateMachineState.context.isNurseConsultationSelected,
    });
    dispatchStateMachineEvent({
      type: eventNames.DEPENDANT_SELECTED,
      input: selectedMember,
    });
  }

  function onDeliveryInfoNextButtonClick(selectedMember: Member) {
    dispatchStateMachineEvent({
      type: eventNames.CONTINUE_BUTTON_CLICKED,
      input: selectedMember,
    });
  }

  function onBackButtonClick() {
    dispatchStateMachineEvent({ type: eventNames.BACK_BUTTON_CLICKED });
  }

  function onRateServiceClick() {
    dispatchStateMachineEvent({
      type: eventNames.RATE_SERVICE_BUTTON_CLICKED,
    });
  }

  function onGoHomeButtonClick() {
    dispatchStateMachineEvent({ type: eventNames.GO_HOME_BUTTON_CLICKED });
  }

  function onOutstandingPaymentContinueButtonClick() {
    dispatchStateMachineEvent({
      type: eventNames.USER_REQUESTED_PAYMENT,
    });
  }

  function onCardPaymentButtonClick() {
    dispatchStateMachineEvent({
      type: eventNames.CARD_PAYMENT_BUTTON_CLICKED,
    });
  }

  function onOneVoucherContinueButtonClick(oneVoucherPin: string) {
    dispatchStateMachineEvent({
      type: eventNames.ONE_VOUCHER_CONTINUE_BUTTON_CLICKED,
      input: oneVoucherPin,
    });
  }

  function onActiveConsultationSelected(
    consultation: GetMemberConsultationsItem
  ) {
    dispatchStateMachineEvent({
      type: eventNames.ACTIVE_CONSULTATION_SELECTED,
      input: consultation,
    });

    if (
      consultation.outcome ===
        MemberConsultationOutcome.DIGITAL_CONSULT_STARTED ||
      consultation.outcome === MemberConsultationOutcome.TRIAGE_STARTED ||
      consultation.outcome === MemberConsultationOutcome.SESSION_STARTED ||
      consultation.outcome === MemberConsultationOutcome.TRIAGE_ENDED ||
      consultation.outcome === MemberConsultationOutcome.PAYMENT_SUCCEEDED ||
      consultation.outcome === MemberConsultationOutcome.NO_REFUND
    ) {
      dispatchStateMachineEvent({ type: eventNames.USER_REDIRECTED });
    }
  }

  function onActiveConsultationsStartNewConsultation() {
    dispatchStateMachineEvent({
      type: eventNames.START_CONSULTATION_BUTTON_CLICKED,
    });
  }

  function onContinueButtonClick() {
    dispatchStateMachineEvent({ type: eventNames.CONTINUE_BUTTON_CLICKED });
  }

  function onPromoOrVoucherContinueButtonClick(
    selectedPromoOrVoucher?: string
  ) {
    dispatchStateMachineEvent({
      type: eventNames.CONTINUE_BUTTON_CLICKED,
      input: selectedPromoOrVoucher,
    });
  }

  function onPromptContinueButtonClick() {
    trackEvent({
      event: "action.nextStepsContinue",
      source: "Medical Assistance",
    });
    onContinueButtonClick();
  }

  function onDirectToDoctorPromptContinueButtonClick() {
    onContinueButtonClick();
  }

  function onPromptBackButtonClick() {
    trackEvent({
      event: "action.nextStepsBack",
      source: "Medical Assistance",
    });
    onBackButtonClick();
  }

  function onRatingTryAgain() {
    trackEvent({
      event: "action.submitRatingRetry",
      source: "Medical Assistance",
    });
    dispatchStateMachineEvent({ type: eventNames.RATING_TRY_AGAIN });
  }

  function onRatingDoneButtonClick() {
    dispatchStateMachineEvent({ type: eventNames.CONTINUE_BUTTON_CLICKED });
  }

  function onSendRatingFeedback(userRating: UserRating) {
    dispatchStateMachineEvent({
      type: eventNames.CONTINUE_BUTTON_CLICKED,
      input: userRating,
    });
  }

  function onSkipRatingFeedback() {
    trackEvent({
      event: "action.submitRatingSkipped",
      source: "Medical Assistance",
    });
    dispatchStateMachineEvent({ type: eventNames.GO_HOME_BUTTON_CLICKED });
  }

  function onRefundContinueButtonClick() {
    dispatchStateMachineEvent({ type: eventNames.CONTINUE_BUTTON_CLICKED });
  }

  function trackEventBasedOnState(stateMachineState: any) {
    const statesToTrack = [
      "outsideOperatingHours",
      "submitRatingError",
      "submitRatingComplete",
      "selectingDependents",
      "outstandingPayment",
      "outstandingRefund",
      "paymentFailure",
      "findADoctor",
      "findAHospital",
    ];

    const stateMachineStateValue = stateMachineState.value;

    if (statesToTrack.includes(stateMachineStateValue)) {
      trackEvent({
        event: `action.${stateMachineStateValue}`,
        source: "Medical Assistance",
      });
    }
  }

  function getBreakoutDisclaimerRedirectUrl() {
    let redirectURL;
    if (stateMachineState.context.queryParamConsultationId) {
      redirectURL =
        stateMachineState.context.consultationOutcomeResponse?.redirectUrl;
    } else {
      redirectURL = stateMachineState.context.selectedConsultation?.redirectUrl;
    }

    return redirectURL;
  }

  function onBreakoutDisclaimerContinue() {
    dispatchStateMachineEvent({ type: eventNames.USER_REDIRECTED });
  }

  function getSelectedMemberInsightsContext() {
    const selectedDependent = stateMachineState.context.selectedDependent;
    return {
      member_id:
        selectedDependent?.memberId || state.currentUser.memberId || "",
      membershipType: selectedDependent
        ? MembershipType.Dependent
        : MembershipType.MainMember,
    };
  }

  function onSelectDirectToDoctor() {
    const { member_id, membershipType } = getSelectedMemberInsightsContext();
    trackSelectConsultType({
      serviceType: MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT,
      member_id,
      membershipType,
      event_datetime: new Date().toISOString(),
    });
    log.doctorEvent("Direct to Doctor selected", {
      currentState: stateMachineState.value,
      selectedDependent: stateMachineState.context.selectedDependent,
      hasDependents: stateMachineState.context.memberDependantsList?.length > 0,
    });
    dispatchStateMachineEvent({
      type: eventNames.DIRECT_TO_DOCTOR_BUTTON_CLICKED,
    });
  }

  function onSelectDocMedBundle() {
    const { member_id, membershipType } = getSelectedMemberInsightsContext();
    trackSelectConsultType({
      serviceType:
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_MEDICINE_CONSULT,
      member_id,
      membershipType,
      event_datetime: new Date().toISOString(),
    });
    dispatchStateMachineEvent({
      type: eventNames.DOCTOR_MEDICINE_CONSULT_BUTTON_CLICKED,
    });
  }

  function onSelectNurseConsult() {
    const { member_id, membershipType } = getSelectedMemberInsightsContext();
    trackSelectConsultType({
      serviceType:
        MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION,
      member_id: member_id,
      membershipType,
      event_datetime: new Date().toISOString(),
    });
    log.nurseEvent("Nurse Consult selected", {
      currentState: stateMachineState.value,
      selectedDependent: stateMachineState.context.selectedDependent,
      hasDependents: stateMachineState.context.memberDependantsList?.length > 0,
    });
    dispatchStateMachineEvent({
      type: eventNames.NURSE_CONSULTATION_BUTTON_CLICKED,
    });
  }

  function onFindADoctorButtonClick() {
    dispatchStateMachineEvent({
      type: eventNames.FIND_A_DOCTOR_BUTTON_CLICKED,
    });
  }

  function onSeeAllConditionsClick() {
    dispatchStateMachineEvent({
      type: eventNames.SEE_CONDITIONS_BUTTON_CLICKED,
    });
  }

  function onVoucherButtonClick() {
    dispatchStateMachineEvent({
      type: eventNames.VOUCHER_BUTTON_CLICKED,
    });
  }

  function onOneVoucherButtonClick() {
    dispatchStateMachineEvent({
      type: eventNames.ONE_VOUCHER_BUTTON_CLICKED,
    });
  }

  function onPrimarySymptomButtonClick(selectedPrimarySymptomId: string) {
    dispatchStateMachineEvent({
      type: eventNames.PRIMARY_SYMPTOM_BUTTON_CLICKED,
      input: selectedPrimarySymptomId,
    });
  }

  function returnServiceOfferingType() {
    if (stateMachineState.context.isDirectToDoctorSelected) {
      return "DirectToDoctor";
    } else if (stateMachineState.context.isNurseConsultationSelected) {
      return "NurseConsult";
    } else if (stateMachineState.context.isDoctorMedicineBundleSelected) {
      return "DoctorMedicineBundle";
    }
    return "DirectToDoctor";
  }

  function getFilteredServiceOfferingUsageItems(
    voucherKey: keyof MemberServiceOfferingUsage
  ): MemberServiceOfferingUsage[] {
    const typeToChargeCode: Record<
      string,
      MemberProductPaymentRatesChargeCode
    > = {
      isDirectToDoctorSelected:
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT,
      isNurseConsultationSelected:
        MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION,
      isDoctorMedicineBundleSelected:
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT,
    };

    const selectedType = Object.keys(typeToChargeCode).find(
      (key) => (stateMachineState.context as any)[key]
    );

    if (!selectedType) {
      return [];
    }

    const chargeCode = typeToChargeCode[selectedType];
    const offeringId =
      stateMachineState.context.memberProfileResponse?.consultation?.consultationRates.find(
        (rate) => rate.chargeCode === chargeCode
      )?.offeringId || "";

    if (!offeringId) {
      return [];
    }

    return (stateMachineState.context.memberServiceOfferingUsages || [])
      .filter((item) => {
        return (
          item.serviceOfferingType === MemberServiceOfferingType.ALL_SERVICES ||
          item.offeringId === offeringId
        );
      })
      .filter(
        (item) =>
          Object.prototype.hasOwnProperty.call(item, voucherKey) &&
          item[voucherKey] !== null
      );
  }

  function getConsultationAmountAndChargeCode(): {
    amount: string;
    chargeCode: string;
  } {
    const typeToChargeCode: Record<
      string,
      MemberProductPaymentRatesChargeCode
    > = {
      isDirectToDoctorSelected:
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT,
      isNurseConsultationSelected:
        MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION,
      isDoctorMedicineBundleSelected:
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT,
    };

    const selectedType = Object.keys(typeToChargeCode).find(
      (key) => (stateMachineState.context as any)[key]
    );

    if (!selectedType) {
      return { amount: "0", chargeCode: "" };
    }

    const chargeCode = typeToChargeCode[selectedType];
    const amount =
      stateMachineState.context.memberProfileResponse?.consultation?.consultationRates.find(
        (rate) => rate.chargeCode === chargeCode
      )?.amountFormatted || "0";
    return { amount, chargeCode };
  }

  function onViewPaymentReceiptClick() {
    dispatchStateMachineEvent({
      type: eventNames.VIEW_RECEIPT_BUTTON_CLICKED,
    });
  }

  function getSelectedDependent(): MembershipDependent | null {
    const memberId =
      stateMachineState.context.paymentReceiptResponse?.patientMemberId;

    const selectedDependent =
      stateMachineState.context.selectedDependent || null;

    if (memberId === state.currentUser.memberId) {
      return {
        memberId: state.currentUser.memberId,
        firstName: state.currentUser.firstName,
        lastName: state.currentUser.lastName,
        identityDocumentType: null,
        identityDocumentValue: null,
        sexAtBirth: "",
        dateOfBirth: "",
        identityDocumentCountryOfIssue: "",
        status: "active",
      };
    }

    if (!selectedDependent) {
      return (
        stateMachineState.context.memberDependantsList.find(
          (member) => member.memberId === memberId
        ) || null
      );
    }

    return selectedDependent;
  }

  function onDiscountVoucherSelected(
    selectedVoucher: MemberServiceOfferingUsage | null
  ) {
    dispatchStateMachineEvent({
      type: eventNames.DISCOUNT_VOUCHER_SELECTED,
      input: selectedVoucher || null,
    });
  }

  function onApplyDiscountVoucher(
    voucherCode: string,
    isUseVoucherLater?: boolean
  ) {
    dispatchStateMachineEvent({
      type: eventNames.APPLY_DISCOUNT_VOUCHER,
      input: { voucherCode, isUseVoucherLater },
    });
  }

  function onAddDiscountVoucherClicked() {
    dispatchStateMachineEvent({
      type: eventNames.ADD_A_DISCOUNT_VOUCHER_CLICKED,
    });
  }

  useEffect(() => {
    log.stateChange("State changed", {
      currentState: stateMachineState.value,
      selectedDependent: stateMachineState.context.selectedDependent,
      isDirectToDoctorSelected:
        stateMachineState.context.isDirectToDoctorSelected,
      isNurseConsultationSelected:
        stateMachineState.context.isNurseConsultationSelected,
      isDoctorMedicineBundleSelected:
        stateMachineState.context.isDoctorMedicineBundleSelected,
      hasDependents: stateMachineState.context.memberDependantsList?.length > 0,
      dependentsCount: stateMachineState.context.memberDependantsList?.length,
    });

    setIsLoading(
      stateMachineState.matches("fetchingConsultationOutcome") ||
        stateMachineState.matches("startingConsultation") ||
        stateMachineState.matches("waitingForConsultationOutcome") ||
        stateMachineState.matches("fetchingMemberDependants") ||
        stateMachineState.matches("fetchingMemberProfile") ||
        stateMachineState.matches("processingConsultationOutcome") ||
        stateMachineState.matches("fetchingPaymentReceipt") ||
        stateMachineState.matches("applyingDiscountVoucher") ||
        stateMachineState.matches("refreshingPaymentServiceOfferings")
    );
    trackEventBasedOnState(stateMachineState);

    if (stateMachineState.matches("start")) {
      trackApplicationEvent(AnalyticsEvent.APPLICATION_START, {
        applicationName: "Application: intercare medical assistance",
        applicationStep: "step 1",
      });
    }

    if (stateMachineState.matches("processingConsultationOutcome")) {
      trackApplicationEvent(AnalyticsEvent.APPLICATION_COMPLETE, {
        applicationName: "Application: intercare medical assistance",
        applicationStep: "step 3",
      });
    }
  }, [stateMachineState, stateMachineState.context]);

  return (
    <MaxWidthContainer>
      <Stack
        spacing={2}
        justifyContent="space-between"
        sx={{ minHeight: "100%", overflowX: "hidden" }}
      >
        {stateMachineState.matches("waitingForRequestPaymentOutcome") && (
          <FullscreenLoadingIndicator
            loadingText={t("common.pleaseWait")}
            mainIconType="face"
          />
        )}

        {stateMachineState.matches("promptingNurseConsultNextSteps") && (
          <ConsultationPrompt
            onContinueButtonClick={onPromptContinueButtonClick}
            onBackButtonClick={onPromptBackButtonClick}
            onFindADoctorButtonClick={onFindADoctorButtonClick}
            selectedDependant={
              stateMachineState.context.selectedDependent || null
            }
            consultationType="NurseConsult"
            selectedVoucherOrPromo={
              stateMachineState.context.selectedVoucherOrPromo
            }
          />
        )}

        {(stateMachineState.matches("promptingDirectToDoctorNextSteps") ||
          stateMachineState.matches(
            "promptingDirectToDoctorNextStepsWithPromoCode"
          )) && (
          <ConsultationPrompt
            onContinueButtonClick={onDirectToDoctorPromptContinueButtonClick}
            onBackButtonClick={onPromptBackButtonClick}
            onFindADoctorButtonClick={onFindADoctorButtonClick}
            selectedDependant={
              stateMachineState.context.selectedDependent || null
            }
            selectedVoucherOrPromo={
              stateMachineState.context.selectedVoucherOrPromo
            }
          />
        )}

        {(stateMachineState.matches("selectConsultationType") ||
          stateMachineState.matches("promptingPromoCode")) &&
          stateMachineState.context?.memberProfileResponse && (
            <ConsultationTypeSelection
              onSeeAllConditionsClick={onSeeAllConditionsClick}
              onDirectToDoctorConsultClick={onSelectDirectToDoctor}
              onNurseConsultClick={onSelectNurseConsult}
              onDocMedBundleClick={onSelectDocMedBundle}
              memberProfile={stateMachineState.context.memberProfileResponse}
              onBackButtonClick={
                isFeatureEnabled(
                  FeatureToggleIdentifier.ENABLE_SINGLE_CONSULTATION_ID
                )
                  ? onBackButtonClick
                  : undefined
              }
            />
          )}

        {stateMachineState.matches("showingDocMedBundlesInfo") && (
          <DocMedsBundlesInfo
            onBackButtonClick={onBackButtonClick}
            onContinueButtonClick={onContinueButtonClick}
            contractsToSign={stateMachineState.context.contractsToSign}
            memberProfile={stateMachineState.context.memberProfileResponse}
          />
        )}

        {stateMachineState.matches("showingDeliveryInfo") &&
          stateMachineState.context?.memberProfileResponse && (
            <DocMedsDeliveryInfo
              memberProfile={stateMachineState.context.memberProfileResponse}
              onBackButtonClick={onBackButtonClick}
              onNextButtonClick={onDeliveryInfoNextButtonClick}
              dependants={stateMachineState.context.memberDependantsList || []}
            />
          )}

        {stateMachineState.matches("showPrimarySymptomsList") &&
          stateMachineState.context?.memberProfileResponse &&
          stateMachineState.context?.primarySymptomsResponse && (
            <>
              <DirectToDoctorPrimarySymptoms
                primarySymptoms={
                  stateMachineState.context.primarySymptomsResponse
                }
                onBackButtonClick={onBackButtonClick}
                onPrimarySymptomButtonClick={onPrimarySymptomButtonClick}
                memberProfile={stateMachineState.context.memberProfileResponse}
                memberId={
                  stateMachineState.context.selectedDependent?.memberId ||
                  state.currentUser.memberId ||
                  ""
                }
                membershipType={
                  stateMachineState.context.selectedDependent
                    ? MembershipType.Dependent
                    : MembershipType.MainMember
                }
              />
            </>
          )}

        {(stateMachineState.matches("promptingPromoCode") ||
          stateMachineState.matches(
            "promptingDirectToDoctorNextStepsWithPromoCode"
          )) &&
          stateMachineState.context.memberServiceOfferingUsages && (
            <AvailablePromoCodesModal
              serviceOfferingUsageItems={
                stateMachineState.context.memberServiceOfferingUsages
              }
              isModalOpen={true}
              onContinueButtonClick={onPromoOrVoucherContinueButtonClick}
              onSkipButtonClick={() =>
                dispatchStateMachineEvent({
                  type: eventNames.SKIP_BUTTON_CLICKED,
                })
              }
              onModalClose={onBackButtonClick}
              selectedConsultationType={
                stateMachineState.context.isDirectToDoctorSelected
                  ? MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
                  : stateMachineState.context.isNurseConsultationSelected
                    ? MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
                    : MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_MEDICINE_CONSULT
              }
            />
          )}

        {stateMachineState.matches("promptingNurseConsultMoreInfo") && (
          <NurseConsultMoreInfo onBackButtonClick={onBackButtonClick} />
        )}

        {stateMachineState.matches("outstandingPayment") && (
          <ActiveConsultationOutstandingPayment
            priceFormatted={
              stateMachineState.context.selectedConsultation
                ?.chargeAmountFormatted
            }
            chargeCode={
              stateMachineState.context.selectedConsultation?.chargeCode
            }
            onContinue={onOutstandingPaymentContinueButtonClick}
            onBack={onBackButtonClick}
          />
        )}

        {stateMachineState.matches("outstandingRefund") && (
          <Refund
            priceFormatted={
              stateMachineState.context.selectedConsultation
                ?.chargeAmountFormatted
            }
            onContinue={onRefundContinueButtonClick}
            onBack={onBackButtonClick}
          />
        )}

        {stateMachineState.matches("paymentFailure") && (
          <PaymentFailure onContinue={onGoHomeButtonClick} />
        )}

        {stateMachineState.matches("handlingPaymentFailure") && (
          <PaymentFailure
            onContinue={onGoHomeButtonClick}
            onRetry={onContinueButtonClick}
            onBackHome={onBackButtonClick}
          />
        )}

        {stateMachineState.matches("selectingDependents") &&
          stateMachineState?.context?.memberDependantsList && (
            <Stack p={2} height="100vh" justifyContent="end">
              <MedicalAssistanceDependantSelection
                onNextButtonClick={onDependentSelectionNextButtonClick}
                dependants={stateMachineState.context.memberDependantsList}
              />
            </Stack>
          )}

        {stateMachineState.matches("selectingActiveConsultation") && (
          <ActiveConsultations
            consultations={
              stateMachineState.context.activeConsultationListResponse
            }
            availabilityData={
              stateMachineState.context.consultationAvailabilityData
            }
            onActiveConsultationSelected={onActiveConsultationSelected}
            onStartNewConsultation={onActiveConsultationsStartNewConsultation}
          />
        )}

        {stateMachineState.matches("showingPaymentOptions") && (
          <PaymentOptionSelection
            onCardPaymentButtonClick={onCardPaymentButtonClick}
            onBack={onBackButtonClick}
            isBuyCareCardVoucher={false}
            onVoucherButtonClick={onVoucherButtonClick}
            onOneVoucherButtonClick={onOneVoucherButtonClick}
            serviceOfferingUsageItems={getFilteredServiceOfferingUsageItems(
              VoucherFilterKey.VoucherDetails
            )}
            discountVouchers={getFilteredServiceOfferingUsageItems(
              VoucherFilterKey.DiscountVoucherDetails
            )}
            selectedService={getConsultationAmountAndChargeCode()}
            onDiscountVoucherSelected={onDiscountVoucherSelected}
            selectedDiscountVoucher={
              stateMachineState.context.selectedDiscountVoucher
            }
            onApplyDiscountVoucher={onApplyDiscountVoucher}
            applyDiscountVoucherResponseError={
              stateMachineState.context.applyDiscountVoucherResponseError
            }
            applyDiscountVoucherResponse={
              stateMachineState.context.applyDiscountVoucherResponse
            }
            capturedDiscountVoucherCode={
              stateMachineState.context.capturedDiscountVoucherCode
            }
            onAddDiscountVoucherClicked={onAddDiscountVoucherClicked}
            isUseVoucherLater={stateMachineState.context.isUseVoucherLater}
          />
        )}

        {stateMachineState.matches("selectingVoucher") && (
          <VoucherSelection
            onBackButtonClick={onBackButtonClick}
            onContinueButtonClick={onPromoOrVoucherContinueButtonClick}
            serviceOfferingUsageItems={getFilteredServiceOfferingUsageItems(
              "voucherDetails"
            )}
          />
        )}

        {stateMachineState.matches("showingOneVoucherRedemption") && (
          <OneVoucherRedemption
            memberProfile={
              stateMachineState.context.memberProfileResponse || undefined
            }
            serviceOfferingType={returnServiceOfferingType()}
            onBackButtonClick={onBackButtonClick}
            onOneVoucherContinueButtonClick={onOneVoucherContinueButtonClick}
            currentOneVoucherPin={stateMachineState.context.oneVoucherPin || ""}
            responseError={
              stateMachineState.context.oneVoucherRedemptionError || ""
            }
            discountAmount={
              stateMachineState.context.selectedDiscountVoucher
                ?.discountVoucherDetails?.discountAmount || 0
            }
            selectedServiceAmount={getConsultationAmountAndChargeCode().amount}
          />
        )}

        {stateMachineState.matches("genericError") && (
          <Stack height="100vh" justifyContent="end">
            <DefaultError onComeBackLater={onGoHomeButtonClick}>
              <Typography variant="h2">{t("GenericError.title")}</Typography>
              <Typography pb={2}>{t("GenericError.content")}</Typography>
            </DefaultError>
          </Stack>
        )}

        {stateMachineState.matches("outsideOperatingHours") && (
          <Stack sx={{ height: "100vh" }}>
            <OutsideConsultationOperatingHours
              availabilityData={
                stateMachineState.context.consultationAvailabilityData
              }
              shouldDisplayViewConsultationHistoryCTA={
                stateMachineState.context.selectedConsultation?.outcome ===
                MemberConsultationOutcome.PAYMENT_SUCCEEDED
              }
              redirectUrl={
                stateMachineState.context.selectedConsultation?.redirectUrl
              }
            />
          </Stack>
        )}

        {stateMachineState.matches("promptingFindADoctor") && (
          <Stack sx={{ height: "100vh" }}>
            <DoctorBookingConfirmation
              onGoBackHome={onBackButtonClick}
              onContinue={onContinueButtonClick}
            />
          </Stack>
        )}

        {stateMachineState.matches("findADoctor") && (
          <Stack sx={{ minHeight: "100vh" }}>
            <FindADoctor onBackButton={onBackButtonClick} />
          </Stack>
        )}

        {stateMachineState.matches("findAHospital") && (
          <Stack sx={{ minHeight: "100vh" }}>
            <MedicalAssistanceFindAHospital />
          </Stack>
        )}

        {stateMachineState.matches("serviceRating") && (
          <Stack sx={{ minHeight: "100vh" }}>
            <UserRatingFeedback
              isFromOrderSummary={
                stateMachineState.context.isFromOrderSummary || false
              }
              onSkipFeedback={onSkipRatingFeedback}
              serviceType={ServiceRating.CONSULTATION}
              ratedServiceId={
                stateMachineState.context.selectedConsultation
                  ?.consultationId ?? ""
              }
              onSendFeedback={onSendRatingFeedback}
            />
          </Stack>
        )}

        {stateMachineState.matches("submitRatingError") && (
          <UserRatingFeedbackError onTryAgain={onRatingTryAgain} />
        )}

        {stateMachineState.matches("submitRatingComplete") && (
          <ThankYou
            title={t("UserRatingFeedback.thankYou.title")}
            subTitle={t("UserRatingFeedback.thankYou.subtitle")}
            buttonLabel={t("common.backHomeButton")}
            onButtonClick={onRatingDoneButtonClick}
          />
        )}

        {stateMachineState.matches("exit") && <Navigate to="/" />}

        {stateMachineState.matches("fetchingMemberDetails") && (
          <Stack height="100%">
            <FullscreenLoadingIndicator mainIconType="face" />
          </Stack>
        )}

        {stateMachineState.matches("breakoutDisclaimer") && (
          <Stack sx={{ height: "98vh" }}>
            {stateMachineState.context.consultationPaymentMemberLedgerId !=
            null ? (
              <Breakout
                type="Ecentric"
                onConfirm={onBreakoutDisclaimerContinue}
                onDecline={onBackButtonClick}
                redirectUrl={getConsultationPaymentRedirectURL(
                  stateMachineState.context.consultationPaymentMemberLedgerId
                )}
              />
            ) : (
              <Breakout
                type="Intercare"
                onConfirm={onBreakoutDisclaimerContinue}
                redirectUrl={getBreakoutDisclaimerRedirectUrl()}
                onDecline={onBackButtonClick}
              />
            )}
          </Stack>
        )}

        {stateMachineState.matches("showOrderSummaryScreen") &&
          stateMachineState.context.paymentReceiptResponse && (
            <ConsultationOrderSummary
              consultationId={
                stateMachineState.context.queryParamConsultationId
              }
              consultationPaymentReceipt={
                stateMachineState.context.paymentReceiptResponse
                  ? stateMachineState.context.paymentReceiptResponse
                  : null
              }
              onBackButtonClick={onBackButtonClick}
              onViewReceiptClick={onViewPaymentReceiptClick}
              onRateServiceClick={onRateServiceClick}
              selectedDependant={getSelectedDependent()}
            />
          )}

        {stateMachineState.matches("showingPaymentReceipt") &&
          stateMachineState.context.paymentReceiptResponse && (
            <PaymentReceipt
              amount={
                stateMachineState.context.paymentReceiptResponse.paymentAmount
              }
              currency={
                stateMachineState.context.paymentReceiptResponse.currencyCode
              }
              transactionDate={
                stateMachineState.context.paymentReceiptResponse
                  .paymentUtcDateTime
              }
              referenceNumber={
                stateMachineState.context.paymentReceiptResponse
                  .transactionReferenceNumber
              }
              merchantId={
                stateMachineState.context.paymentReceiptResponse.unuMerchantId
              }
              cardNumber={
                stateMachineState.context.paymentReceiptResponse.cardNumber ||
                ""
              }
              onBackButtonClick={onBackButtonClick}
              isDiscountApplied={
                stateMachineState.context.paymentReceiptResponse
                  .isDiscountApplied
              }
              originalAmount={
                stateMachineState.context.paymentReceiptResponse.originalAmount
              }
              voucherTypeName={
                stateMachineState.context.paymentReceiptResponse.voucherName ||
                ""
              }
              voucherCode={
                stateMachineState.context.paymentReceiptResponse.voucherCode ||
                ""
              }
              paymentMethod={
                stateMachineState.context.paymentReceiptResponse.paymentMethod
              }
              oneVoucherDetails={
                stateMachineState.context.paymentReceiptResponse
                  .oneVoucherDetails
              }
            />
          )}

        {stateMachineState.matches("exitToMyPurchases") && (
          <Navigate
            to="/home/more/my-purchases"
            state={{ fromMyPurchasesPage: fromMyPurchasesPage ?? 1 }}
          />
        )}

        {isLoading && (
          <Stack height="100%">
            <FullscreenLoadingIndicator
              loadingText={t("common.pleaseWait")}
              mainIconType="face"
            >
              <Stack pt={5}>
                <Button
                  variant="outlined"
                  onClick={onGoHomeButtonClick}
                  color="primary"
                  fullWidth
                >
                  {t("common.goHomeButton")}
                </Button>
              </Stack>
            </FullscreenLoadingIndicator>
          </Stack>
        )}
      </Stack>
    </MaxWidthContainer>
  );
}
